/* ==========================================================================
   motion.js — GSAP motion system (SPEC v3 §2.4 — supersedes v2 §14.2 scope).
   Loads sitewide after idle. Reference bar: Represent / Polène / Kith.
   Inventory:
   1. Hero entrance: media settle + line-split headline rise
   2. Line-split reveals on [data-split] (hero + campaign titles)
   3. Fade-up reveals: [data-motion] sections + batched grid/card items
   4. Parallax scrub on [data-parallax] media
   5. Image unveil: scale 1.12→1 on section media entering view
   Rules kept from SPEC §14.1: transform/opacity only, once per view,
   full prefers-reduced-motion opt-out, fail-silent (§23.2).
   ========================================================================== */

if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const load = (src) =>
    new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.append(s);
    });

  /* Wrap each word in masked spans (SplitText-free). Element children —
     e.g. <em class="accent-word"> — are preserved as single word units so
     mixed-typography titles survive the split (v4.2). */
  const splitLines = (el) => {
    if (el.dataset.splitDone) return el.querySelectorAll('.split-word');
    el.dataset.splitDone = 'true';
    const tokens = [];
    el.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent.trim().split(/\s+/).filter(Boolean).forEach((w) => tokens.push(w));
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        tokens.push(node.cloneNode(true));
      }
    });
    el.textContent = '';
    tokens.forEach((token, i) => {
      const mask = document.createElement('span');
      mask.className = 'split-line';
      const inner = document.createElement('span');
      inner.className = 'split-word';
      inner.append(token);
      mask.append(inner);
      el.append(mask);
      if (i < tokens.length - 1) el.append(' ');
    });
    return el.querySelectorAll('.split-word');
  };

  const boot = async () => {
    try {
      await load(window.tjpMotion.gsap);
      await load(window.tjpMotion.scrollTrigger);
    } catch {
      return; // motion is enhancement only
    }

    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    const DRAMATIC = 'expo.inOut'; /* ≈ cubic-bezier(.83,0,.17,1) — Polène */
    const SETTLE = 'power3.out';
    gsap.defaults({ ease: SETTLE });

    /* 1 — Hero entrance, once per load */
    const hero = document.querySelector('[data-motion="hero"]');
    if (hero) {
      const media = hero.querySelector('.hero__media img, .hero__media video, .hero__media svg, .cityhero__media img, .cityhero__media svg');
      const title = hero.querySelector('[data-split]');
      const rest = hero.querySelectorAll('.hero__subtitle, .hero .btn, .cityhero__eyebrow, .cityhero__desc, .cityhero__count, .cityhero__mark');
      const glassCard = hero.querySelector('.cityhero__content');
      const tl = gsap.timeline();
      if (media) tl.fromTo(media, { scale: 1.08 }, { scale: 1, duration: 1.4, ease: DRAMATIC }, 0);
      if (glassCard) tl.fromTo(glassCard, { y: 32, opacity: 0 }, { y: 0, opacity: 1, duration: 1.0, ease: SETTLE }, 0.4);
      if (title) {
        tl.fromTo(
          splitLines(title),
          { y: '0.6em', opacity: 0 },
          { y: 0, opacity: 1, duration: 0.9, stagger: 0.07, ease: 'power4.out', clearProps: 'all' },
          0.55
        );
      }
      if (rest.length) {
        tl.fromTo(rest, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, stagger: 0.1 }, 0.8);
      }
    }

    /* 2 — Campaign tiles: unveil + title split on enter */
    document.querySelectorAll('[data-motion="campaign"]').forEach((tile) => {
      const media = tile.querySelector('.campaign__media img, .campaign__media video, .campaign__media svg');
      const title = tile.querySelector('[data-split]');
      const rest = tile.querySelectorAll('.campaign__eyebrow, .campaign__subtitle, .campaign__ctas');
      const tl = gsap.timeline({
        scrollTrigger: { trigger: tile, start: 'top 75%', once: true },
      });
      if (media) tl.fromTo(media, { scale: 1.12 }, { scale: 1, duration: 1.2, ease: DRAMATIC }, 0);
      if (title) {
        tl.fromTo(
          splitLines(title),
          { y: '0.6em', opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, stagger: 0.06, ease: 'power4.out', clearProps: 'all' },
          0.2
        );
      }
      if (rest.length) {
        tl.fromTo(rest, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, stagger: 0.08 }, 0.5);
      }
    });

    /* 3a — Section fade-up reveals ([data-motion="reveal"/"fade"]) */
    document.querySelectorAll('[data-motion="reveal"]').forEach((section) => {
      const items = section.querySelectorAll('[data-motion-item]');
      const targets = items.length ? items : [section];
      gsap.fromTo(
        targets,
        { y: 28, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          stagger: items.length ? 0.06 : 0,
          scrollTrigger: { trigger: section, start: 'top 80%', once: true },
        }
      );
    });

    document.querySelectorAll('[data-motion="fade"]').forEach((section) => {
      gsap.fromTo(
        section,
        { opacity: 0 },
        { opacity: 1, duration: 0.9, scrollTrigger: { trigger: section, start: 'top 85%', once: true } }
      );
    });

    /* 3b — Sitewide grid/card fade-ups (collection, search, journal, recos)
       Batched so rows stagger as they enter (Kith fade-up grammar) */
    const gridItems = document.querySelectorAll(
      '.collection__grid > li, .journal__card, .collectionslist__grid > li'
    );
    if (gridItems.length) {
      gsap.set(gridItems, { y: 28, opacity: 0 });
      ScrollTrigger.batch(gridItems, {
        start: 'top 88%',
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, { y: 0, opacity: 1, duration: 0.8, stagger: 0.06 }),
      });
      /* safety: anything never scrolled into a trigger (e.g. filter
         re-render) becomes visible on refresh */
      ScrollTrigger.addEventListener('refreshInit', () => gsap.set(gridItems, { clearProps: 'opacity,transform' }));
    }

    /* 4 — Parallax scrub on hero/campaign media (SPEC v3 §2.4) */
    document.querySelectorAll('[data-parallax]').forEach((wrap) => {
      const media = wrap.querySelector('img, video, svg');
      if (!media) return;
      gsap.fromTo(
        media,
        { yPercent: -5 },
        {
          yPercent: 5,
          ease: 'none',
          scrollTrigger: {
            trigger: wrap,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    });

    /* 5 — Image unveil on editorial media (image-with-text, matière, story) */
    document
      .querySelectorAll('.iwt__media img, .matiere__media img, .matiere__media video, .lookbook__media img')
      .forEach((img) => {
        gsap.fromTo(
          img,
          { scale: 1.12 },
          {
            scale: 1,
            duration: 1.2,
            ease: DRAMATIC,
            scrollTrigger: { trigger: img, start: 'top 85%', once: true },
          }
        );
      });

    /* Nastaliq mark reveal (D-017) — right-to-left wipe, once */
    document
      .querySelectorAll('.cityindex .nastaliq-mark, .pdp__eyebrow .nastaliq-mark')
      .forEach((mark) => {
        gsap.fromTo(
          mark,
          { clipPath: 'inset(0 0 0 100%)', opacity: 0.4 },
          {
            clipPath: 'inset(0 0 0 0%)',
            opacity: 1,
            duration: 0.8,
            scrollTrigger: { trigger: mark, start: 'top 85%', once: true },
          }
        );
      });

    /* Theme editor: kill triggers on section unload (§18.4). Reveal tweens
       pre-hide their targets (opacity 0), so killing the triggers must also
       restore visibility — otherwise sections stay invisible in the
       customizer / dev hot-reload after any section edit. */
    document.addEventListener('shopify:section:unload', () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
      gsap.set(
        '[data-motion-item], [data-motion="reveal"], [data-motion="fade"], .collection__grid > li, .journal__card, .collectionslist__grid > li',
        { clearProps: 'opacity,transform,visibility' }
      );
    });

    /* 6 - Pill Navigation Interactions (header + category bar) */
    document.querySelectorAll('.header-pill-nav, .ptabs-pill-nav').forEach((pillNav) => {
      const activeBg = pillNav.querySelector('.pill-nav__active-bg');
      const links = pillNav.querySelectorAll('.pill-nav__link');
      if (!activeBg || !links.length) return;

      const moveBgTo = (link, animate = true) => {
        if (!link || !activeBg) return;
        const rect = link.getBoundingClientRect();
        const parentRect = pillNav.getBoundingClientRect();
        const left = rect.left - parentRect.left;
        const width = rect.width;

        if (animate) {
          gsap.to(activeBg, { x: left, width: width, duration: 0.3, ease: 'power2.out' });
        } else {
          gsap.set(activeBg, { x: left, width: width });
        }
      };

      let currentActive = pillNav.querySelector('.pill-nav__link.is-active') || links[0];

      const setActive = (link) => {
        if (!link) return;
        links.forEach((l) => l.classList.remove('is-active', 'ptabs__tab--active'));
        link.classList.add('is-active');
        if (link.classList.contains('ptabs__tab')) link.classList.add('ptabs__tab--active');
        currentActive = link;
        moveBgTo(link);
      };

      if (currentActive) {
        setTimeout(() => moveBgTo(currentActive, false), 50);
      }

      links.forEach((link) => {
        link.addEventListener('mouseenter', () => moveBgTo(link));
        link.addEventListener('mouseleave', () => moveBgTo(currentActive));
        link.addEventListener('click', () => setActive(link));
      });

      window.addEventListener('resize', () => moveBgTo(currentActive, false));
    });
  };

  /* --- Library-free micro-interactions (SPEC v4 §3.4/§3.5) --------------- */
  if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    /* 3D card tilt ≤ 4°, springy return via CSS transition (component-card) */
    document.addEventListener('pointermove', (e) => {
      const card = e.target.closest?.('[data-tilt]');
      if (!card) return;
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      card.style.setProperty('--tilt-x', `${(-y * 4).toFixed(2)}deg`);
      card.style.setProperty('--tilt-y', `${(x * 4).toFixed(2)}deg`);
    });
    document.addEventListener(
      'pointerleave',
      (e) => {
        const card = e.target.closest?.('[data-tilt]');
        if (!card) return;
        card.style.setProperty('--tilt-x', '0deg');
        card.style.setProperty('--tilt-y', '0deg');
      },
      true
    );

    /* Magnetic hero CTA: pulls ≤ 8px toward the cursor */
    const magnet = document.querySelector('.hero .btn');
    if (magnet) {
      const zone = 90;
      document.addEventListener('pointermove', (e) => {
        const r = magnet.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.hypot(dx, dy);
        if (dist < zone) {
          const pull = (1 - dist / zone) * 8;
          magnet.style.transform = `translate(${((dx / dist) * pull).toFixed(1)}px, ${((dy / dist) * pull).toFixed(1)}px)`;
        } else if (magnet.style.transform) {
          magnet.style.transform = '';
        }
      });
    }
  }

  if ('requestIdleCallback' in window) requestIdleCallback(boot, { timeout: 2500 });
  else setTimeout(boot, 1200);
}
